<template>
  <v-container fluid class="campaigns-overview">
    <v-layout row wrap>
      <v-flex xs8>
        <DatePicker
          v-model="dateRange"
          type="date"
          range
          placeholder="Select Date Range"
          value-type="format"
          :shortcuts="shortcuts"
          @input="onDateRangeChange"
        ></DatePicker>

        <v-btn
          outline
          dense
          small
          :loading="analyticsBreakdownLoading"
          :disabled="analyticsBreakdownLoading"
          @click="downloadAnalyticsBreakdown"
          >Download Breakdown (XLSX)</v-btn
        >
        <overall-campaigns-pie-chart
          v-if="overallData && overallData.accumulatedMsgsStatus"
          :overall-data="overallData.accumulatedMsgsStatus"
        />
      </v-flex>
      <v-flex xs4>
        <campaigns-numbers
          v-if="overallData && overallData.campaignsDetails"
          :overall-data="overallData"
        />
      </v-flex>

      <v-flex xs12 mt-3>
        <campaigns-bar-chart
          v-if="overallData && overallData.campaignsAnalytics"
          :overall-data="overallData"
        />
      </v-flex>
    </v-layout>

    <v-dialog v-model="analyticsBreakdownDialog" width="600">
      <v-card class="pa-5">
        <v-card-title class="pa-0">Breakdown export queued</v-card-title>
        <v-card-text class="px-0 pb-0">
          We’re generating the requested analytics breakdown. You’ll receive it by
          email once it’s ready.
        </v-card-text>
        <v-card-actions class="px-0">
          <v-spacer />
          <v-btn color="primary" @click="analyticsBreakdownDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  getLastMonthRange,
  getLastThreeDaysRange,
  getLastWeekRange,
  getThisMonthRange,
  getThisWeekRange,
  getTodayRange,
  getYesterdayRange,
} from "../../../../utils/dateRanges";
import campaignsBarChart from "./campaignsBarChart";
import campaignsNumbers from "./campaignsNumbers";
import overallCampaignsPieChart from "./overallCampaignsPieChart";
export default {
  name: "index",
  components: {
    overallCampaignsPieChart,
    campaignsNumbers,
    campaignsBarChart,
    DatePicker,
  },
  data() {
    const initialDateRange = [
      moment(getThisWeekRange()[0]).format("YYYY-MM-DD"),
      moment(getThisWeekRange()[1]).format("YYYY-MM-DD"),
    ];
    return {
      overallData: {},
      analyticsBreakdownLoading: false,
      analyticsBreakdownDialog: false,
      analyticsBreakdownExportId: "",
      dateRange: initialDateRange,
      lastValidDateRange: [...initialDateRange],
      suppressDateRangeChange: false,
      shortcuts: [
        {
          text: "Today",
          onClick: () => getTodayRange(),
        },
        {
          text: "Yesterday",
          onClick: () => getYesterdayRange(),
        },
        {
          text: "Last 3 days",
          onClick: () => getLastThreeDaysRange(),
        },
        {
          text: "This week",
          onClick: () => getThisWeekRange(),
        },
        {
          text: "Last week",
          onClick: () => getLastWeekRange(),
        },
        {
          text: "This month",
          onClick: () => getThisMonthRange(),
        },
        {
          text: "Last month",
          onClick: () => getLastMonthRange(),
        },
      ],
    };
  },
  async mounted() {
    await this.getInsights();
  },
  computed: {},
  methods: {
    async onDateRangeChange(range) {
      if (this.suppressDateRangeChange) {
        return;
      }
      if (!range || !range[0] || !range[1]) {
        return;
      }

      let start = moment(range[0], "YYYY-MM-DD", true);
      let end = moment(range[1], "YYYY-MM-DD", true);

      if (!start.isValid() || !end.isValid()) {
        await this.restoreValidDateRange("Invalid date range");
        return;
      }

      if (start.isAfter(end)) {
        const temp = start;
        start = end;
        end = temp;
      }

      const maxEnd = start.clone().add(3, "months");
      if (end.isAfter(maxEnd)) {
        await this.restoreValidDateRange("Date range can’t exceed 3 months");
        return;
      }

      const normalizedRange = [
        start.format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD"),
      ];

      const shouldUpdateDateRange =
        !this.dateRange ||
        this.dateRange[0] !== normalizedRange[0] ||
        this.dateRange[1] !== normalizedRange[1];
      if (shouldUpdateDateRange) {
        this.suppressDateRangeChange = true;
        this.dateRange = normalizedRange;
        await this.$nextTick();
        this.suppressDateRangeChange = false;
      }

      this.lastValidDateRange = [...normalizedRange];
      await this.getInsights();
    },
    async restoreValidDateRange(message) {
      this.suppressDateRangeChange = true;
      this.dateRange = [...this.lastValidDateRange];
      await this.$nextTick();
      this.suppressDateRangeChange = false;
      if (message) {
        this.$store.dispatch("setSnackbar", {
          text: message,
          color: "error",
        });
      }
    },
    async getInsights() {
      const response = await this.$http_session_manager.get(
        `/campaigns/overall-analytics/?bot_id=${this.$store.state.bot.botId}&start_date=${this.dateRange[0]}&end_date=${this.dateRange[1]}`
      );

      this.overallData = response.data;
    },

    async downloadAnalyticsBreakdown() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        this.$store.dispatch("setSnackbar", {
          text: "Please select a date range",
          color: "error",
        });
        return;
      }

      const from = this.dateRange[0];
      const to = this.dateRange[1];
      const url =
        `/campaigns/analytics-breakdown` +
        `?bot_id=${encodeURIComponent(this.$store.state.bot.botId)}` +
        `&start_date=${encodeURIComponent(from)}` +
        `&end_date=${encodeURIComponent(to)}`;
      this.analyticsBreakdownLoading = true;
      try {
        const response = await this.$http_session_manager.get(url, {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        });

        if (response instanceof Blob) {
          let parsed = null;
          try {
            const text = await response.text();
            parsed = JSON.parse(text);
          } catch (e) {
            parsed = null;
          }
          if (parsed && parsed.success && parsed.data && parsed.data.export_id) {
            this.analyticsBreakdownExportId = parsed.data.export_id;
            this.analyticsBreakdownDialog = true;
            return;
          }

          const objectUrl = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = "campaign-analytics-breakdown.xlsx";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(objectUrl);
          return;
        }

        if (response && response.success && response.data && response.data.export_id) {
          this.analyticsBreakdownExportId = response.data.export_id;
          this.analyticsBreakdownDialog = true;
          return;
        }

        this.$store.dispatch("setSnackbar", {
          text: "Unexpected response while requesting breakdown export",
          color: "error",
        });
      } catch (error) {
        this.$store.dispatch("setSnackbar", {
          text: "Failed to request breakdown export",
          color: "error",
        });
      } finally {
        this.analyticsBreakdownLoading = false;
      }
    },
  },
};
</script>

<style scoped></style>
